import * as querystring from 'querystring';
import { ParsedUrlQuery } from 'querystring';

import brandCmsRoutesConfig from '../../../configurations/brandCmsRoutes.json';
import routesConfig from '../../../configurations/routes.json';
import { Locale } from '../../../core/i18n/Locale';
import { getSiteByLocale } from '../../../core/Site/Site';
import { Currency } from '../../../resources/currency/domain/types/currency';
import { MobileApplicationStore } from '../../hooks/useAppMobile';

export type RouteParams = NodeJS.Dict<RouteParamValue>;
export type RouteParamValue = string | number | string[];
export type BrandCmsRouteName = keyof typeof brandCmsRoutesConfig;
export type Route = RouteSingle | RouteLocalized;
export type RouteHandledByNextjs = Route & {
    rewritePath: string;
};
export type RouteSingle = {
    isFallbackRoute?: true;
    path: string;
    rewritePath?: string;
};
export type RouteLocalized = {
    isFallbackRoute?: true;
    path: RoutePathLocalized;
    rewritePath?: string;
};
export type RoutePathLocalized = {
    [key: string]: string;
};
export type RoutesConfig = {
    [key in RouteName]: Route;
};
export type RouteName = keyof typeof routesConfig;

const BRAND_CMS_URL = 'https://bettertrips.evaneos.com';
const TCARE_TOOLS_URL = 'https://tcare-tools.services.evaneos.com';
const MOBILE_APPLICATION_URL = 'https://mobile.links.evaneos.com';

// Pattern to match path parameters with optional regex suffix
// Examples: :destinationSlug or :itineraryId([0-9]+)
export const PATH_PARAM_PATTERN = ':([a-zA-Z]+)(?:\\([^)]+\\))?';

function allRoutes() {
    return routesConfig as RoutesConfig;
}

export function isLocalizedRoute(route: Route) {
    return typeof route.path === 'object';
}

export function generateUrl(
    routeName: RouteName,
    locale: Locale,
    params: RouteParams = {},
    absolute = false,
) {
    const routes = allRoutes();
    const site = getSiteByLocale(locale);
    const { lang } = site;
    const queryParams = paramsWithoutUndefined(params);
    let { path } = routes[routeName];

    if (typeof path === 'object') {
        path = path[lang];
    }

    if (!path) {
        throw new Error(`Route ${routeName} doesnt exist for lang ${lang}`);
    }

    let url = path.replace(new RegExp(PATH_PARAM_PATTERN, 'g'), (match, pathParamName) => {
        if (!(pathParamName in params)) {
            throw new Error(`Missing path param ${match} to generate ${routeName} route url`);
        }

        const value = params[pathParamName];

        if (value === undefined || value === null || value === '' || Array.isArray(value)) {
            let valueForErrorMessage = String(value);
            if (Array.isArray(value)) valueForErrorMessage = `[${value.join(',')}]`;
            if (value === '') valueForErrorMessage = 'an empty string';
            const otherParams = { ...params };
            delete otherParams[pathParamName];
            throw new Error(
                `Invalid path param ${match} (value is ${valueForErrorMessage}) to generate ${routeName} route url on locale ${locale} (other params: ${JSON.stringify(otherParams)})`,
            );
        }

        delete queryParams[pathParamName];

        return value.toString();
    });

    if (Object.keys(queryParams).length) {
        url = `${url}?${buildURLQuery(queryParams)}`;
    }

    if (absolute) {
        url = site.url + url;
    }

    return url;
}

export function generateRewritePath(routeName: RouteName, params?: ParsedUrlQuery) {
    const routes = allRoutes();
    const { rewritePath } = routes[routeName];
    if (!rewritePath) {
        throw new Error(`Route ${routeName} rewritePath is undefined`);
    }

    const searchParams = new URLSearchParams(querystring.stringify(params));
    const query = searchParams.toString();

    if (query) {
        return `${rewritePath}?${query}`;
    }
    return rewritePath;
}

export function generateUrlMayNotExist(
    routeName: RouteName,
    locale: Locale,
    params?: RouteParams,
    absolute?: boolean,
): string | null {
    try {
        return generateUrl(routeName, locale, params, absolute);
    } catch {
        return null;
    }
}

export function generateBrandUrl(routeName: BrandCmsRouteName, locale: Locale): string | null {
    const route = brandCmsRoutesConfig[routeName];
    const slug = route[locale];

    // Handle when the route is not created for the given locale.
    if (!slug) {
        return null;
    }

    // Handle legacy pages on dedicated brand CMS & domain.
    if (routeName === 'about_us') {
        return BRAND_CMS_URL + slug;
    }

    return generateUrl('brand_pages', locale, { slug }, false);
}

export function generateFooterMobileApplicationUrl(os: MobileApplicationStore): string {
    return `${MOBILE_APPLICATION_URL}/evaneos-site-footer-${os === 'apple' ? 'ios' : 'android'}`;
}

export function generateMobileApplicationUrl(locale: Locale, os: MobileApplicationStore): string {
    const site = getSiteByLocale(locale);

    return `${MOBILE_APPLICATION_URL}/${site.mobileApp[os].storeId}`;
}

export function getTcareToolsUrl() {
    return TCARE_TOOLS_URL;
}

type TcareToolsTripFormParams = {
    destinationSlug: string;
    agencyId: number | null;
    itineraryId: number | null;
    gammesList: Record<string, string> | null;
    hostingList: Record<string, string> | null;
    guideList: Record<string, string> | null;
    siteId: number;
    currency: Currency;
};

export function getTcareToolsTripFormUrl({
    destinationSlug,
    agencyId,
    itineraryId,
    gammesList,
    hostingList,
    guideList,
    siteId,
    currency,
}: TcareToolsTripFormParams): string {
    const baseUrl = getTcareToolsUrl();
    const queryParams = new URLSearchParams();

    queryParams.append('destination', destinationSlug);
    queryParams.append('agencyId', agencyId ? agencyId.toString() : '');
    queryParams.append('itineraryId', itineraryId ? itineraryId.toString() : '');
    queryParams.append('rangeList', JSON.stringify(gammesList ?? {}));
    queryParams.append('hostingList', JSON.stringify(hostingList ?? {}));
    queryParams.append('guideList', JSON.stringify(guideList ?? {}));
    queryParams.append('siteId', siteId.toString());
    queryParams.append('currency', currency);

    return `${baseUrl}/quoteForm?${queryParams.toString()}`;
}

function paramsEntriesWithoutUndefined(
    paramsEntries: Array<[string, RouteParamValue | undefined]>,
): [string, RouteParamValue][] {
    return paramsEntries.filter(
        (param: [string, RouteParamValue | undefined]): param is [string, RouteParamValue] =>
            param[1] !== undefined,
    );
}

function paramsWithoutUndefined(params: RouteParams): { [key: string]: RouteParamValue } {
    return paramsEntriesWithoutUndefined(Object.entries(params)).reduce(
        (filteredParams, [key, value]) => {
            return {
                ...filteredParams,
                [key]: value,
            };
        },
        {},
    );
}

function buildURLQuery(params: RouteParams) {
    return paramsEntriesWithoutUndefined(Object.entries(params))
        .sort(([keyA], [keyB]) => {
            return keyA.localeCompare(keyB);
        })
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return value
                    .sort((a, b) => {
                        return a.localeCompare(b);
                    })
                    .map((val) => {
                        return `${key}=${encodeURIComponent(val)}`;
                    })
                    .join('&');
            }
            return `${key}=${encodeURIComponent(value)}`;
        })
        .join('&');
}
